// import { db } from '@/db/local/dexie-db'
import { useMemo, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import type { FotoIntervento } from 'api'
import { keyBy } from 'lodash-es'
import { MoreHorizontalIcon } from 'lucide-react'

import { useAsyncMemo } from '@/hooks/use-async-memo'
import { useDexieQuery } from '@/hooks/use-dexie-query'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Breadcrumbs } from '@/components/breadcrumbs'
import { PageTitle } from '@/components/layout/page-title'
import { InterventoForm } from '@/features/interventi/components/intervento-form/intervento-form'
import { InterventoFormValues } from '@/features/interventi/components/intervento-form/useInterventoForm'
import { InterventoInfoPanel } from '@/features/interventi/components/intervento-info-panel'
import { InterventoStatoBadge } from '@/features/interventi/components/stato-badge'
import { RepairCorrection } from '@/features/riparazioni/components/repair-correction'
import {
  repairPhotos,
  type StoricoPhaseInfo,
} from '@/features/riparazioni/components/storico-timeline'
import { useWho } from '@/features/riparazioni/use-who'
import { useOperatoreNames } from '@/features/utenti/use-operatore-names'
import { interventoSearchSchema } from '@/routes/_authenticated/interventi/-intervento-search-schema'

export const Route = createFileRoute(
  '/_authenticated/interventi/modifica/$interventoId'
)({
  component: RouteComponent,
  ssr: false,
  validateSearch: interventoSearchSchema,
})

const noFotos: FotoIntervento[] = []

function RouteComponent() {
  const { interventoId } = Route.useParams()
  const [isTimingDialogOpen, setIsTimingDialogOpen] = useState(false)

  // Fetch the intervento
  const intervento = useDexieQuery((db) => db.interventi.get(interventoId))

  // Fetch related foto interventi
  const fotoInterventoList = useDexieQuery((db) =>
    db.foto_interventi
      .where({ intervento_id: interventoId })
      .filter((f) => !f.deleted_at)
      .toArray()
  )

  // The inspection's photos fill the form's four slots; a repair's photos (with
  // `riparazione_id`; `== null` also takes rows pulled before the column) show in
  // the Storico and after the slots, never in the form's values, which a save
  // writes back
  const fotoIntervento = useMemo(
    () => (fotoInterventoList ?? []).filter((f) => f.riparazione_id == null),
    [fotoInterventoList]
  )
  // A repair's photos under Immagini too, after the slots, with the page's one
  // carousel (Immagini's): the Storico's thumbnails open it, and on a phone the
  // page mounts the panel twice (the hidden column, the Dettagli dialog)
  const repairFotos = useMemo(
    () =>
      intervento ? repairPhotos(intervento, fotoInterventoList ?? []) : [],
    [intervento, fotoInterventoList]
  )
  const names = useOperatoreNames()
  const who = useWho()
  // The form is the inspector's and the admins'; anyone else here repairs, and
  // the server ignores their edits to the row
  const readOnly =
    !who || (!who.admin && intervento?.operatore_id !== who.userId)

  const fotos = fotoInterventoList ?? noFotos
  const repair = useMemo(
    () => (who ? { who, fotos, names } : undefined),
    [who, fotos, names]
  )

  const phaseActions = (phase: StoricoPhaseInfo) =>
    who && intervento && phase.kind === 'repair' && phase.closingId ? (
      <RepairCorrection
        intervento={intervento}
        repair={phase}
        fotos={fotos}
        who={who}
      />
    ) : null

  const defaultValues = useAsyncMemo<
    Partial<InterventoFormValues>
  >(async () => {
    const fotoInterventoByCategory = keyBy(fotoIntervento, 'categoria')

    return {
      intervento: intervento,
      fotoIntervento: fotoInterventoByCategory,
    }
  }, [fotoIntervento, intervento])

  return (
    <>
      <Breadcrumbs
        items={[
          { name: 'Interventi', to: '/interventi' },
          { name: `Intervento ${intervento?.id_interno ?? interventoId}` },
        ]}
      />

      {/* Responsive layout with consistent column structure */}
      <div className="flex flex-col lg:flex-row lg:gap-8">
        {/* Main content area - title and form */}
        <div className="lg:w-2/3">
          <PageTitle className="mb-6 sm:mb-8">
            <div>Intervento {intervento?.id_interno}</div>

            <div className="mt-3 flex flex-wrap items-center gap-2 lg:hidden">
              {intervento?.status && (
                <InterventoStatoBadge
                  className="rounded-full px-3 py-1 text-sm font-semibold"
                  intervento={intervento}
                />
              )}

              {/* Timing button for mobile */}
              {intervento && intervento.id && (
                <Dialog
                  open={isTimingDialogOpen}
                  onOpenChange={setIsTimingDialogOpen}
                >
                  <DialogTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      aria-label="Dettagli intervento"
                      className="h-9 w-9 rounded-full border-slate-200 bg-white p-0 text-slate-600 shadow-sm hover:bg-slate-50"
                    >
                      <MoreHorizontalIcon className="h-4 w-4" />
                    </Button>
                  </DialogTrigger>
                  {/* The Storico can outgrow the screen: it scrolls under
                      the title */}
                  <DialogContent className="flex max-h-[85dvh] max-w-sm flex-col gap-0 overflow-hidden p-0">
                    <DialogHeader className="shrink-0 px-6 pt-6 pb-4">
                      <DialogTitle>Dettagli intervento</DialogTitle>
                    </DialogHeader>
                    <InterventoInfoPanel
                      className="min-h-0 overflow-y-auto"
                      intervento={intervento}
                      fotos={fotoInterventoList}
                      names={names}
                      variant="dialog"
                      ownCarousel={false}
                      phaseActions={phaseActions}
                    />
                  </DialogContent>
                </Dialog>
              )}
            </div>
          </PageTitle>

          <InterventoForm
            defaultValues={defaultValues}
            morePhotos={repairFotos}
            readOnly={readOnly}
            repair={repair}
          />
        </div>

        {/* Info panel - only visible on desktop for saved interventions */}
        <div className="hidden lg:block lg:w-1/3">
          {intervento && intervento.id ? (
            <div className="sticky top-4 mt-4">
              <InterventoInfoPanel
                intervento={intervento}
                fotos={fotoInterventoList}
                names={names}
                ownCarousel={false}
                phaseActions={phaseActions}
              />
            </div>
          ) : null}
        </div>
      </div>
    </>
  )
}
